<template>
  <div class="qrcode-page" id="qrcode-capture">
    <header class="qrcode-header">
      <van-icon name="arrow-left" class="back-button" @click="goBack" />
      <span>业务员注册二维码</span>
    </header>

    <main class="qrcode-card">
      <p class="qrcode-hint">业务员使用微信扫码即可进入注册页面</p>

      <div class="org-block">
        <span class="org-label">所属机构</span>
        <strong class="org-name">{{ orgName || "机构信息加载中" }}</strong>
        <span v-if="orgId" class="org-id">机构编号 {{ orgId }}</span>
      </div>

      <div class="qrcode-image-wrap">
        <img
          ref="qrImage"
          :src="qrImageUrl"
          alt="业务员注册二维码"
          class="qrcode-image"
          @load="qrLoadFailed = false"
          @error="qrLoadFailed = true"
        />
        <p v-if="qrLoadFailed" class="qrcode-error">二维码加载失败，请刷新页面重试</p>
      </div>

      <div class="link-block">
        <p class="link-label">链接地址（已带机构ID）</p>
        <p class="link-content">{{ link }}</p>
      </div>

      <div class="action-row">
        <button class="action-button" @click="copyLink">
          <van-icon name="link-o" /> 复制链接
        </button>
        <button class="action-button" :disabled="buildingPoster || !link" @click="downloadQr">
          <van-icon name="down" /> {{ buildingPoster ? "生成中…" : "下载注册分享图" }}
        </button>
      </div>

      <p class="capture-tip">
        下载的图片包含机构名称、扫码用途和二维码，可直接发给业务员。注册后自动关联当前机构。
      </p>
    </main>

    <van-popup v-model="showPoster" round class="poster-popup" :close-on-click-overlay="false">
      <div class="poster-header"><strong>业务员注册分享图</strong><button type="button" aria-label="关闭分享图" @click="showPoster = false">关闭</button></div>
      <div class="poster-image-wrap"><img v-if="posterUrl" :src="posterUrl" alt="包含机构名称、注册说明和二维码的业务员注册分享图" class="poster-image" /></div>
      <p class="poster-save-tip">{{ inWechat ? "请长按上方图片，保存到手机或发送给业务员" : "可保存图片分享给业务员；手机也可长按图片保存" }}</p>
      <button v-if="!inWechat" type="button" class="action-button poster-save-button" @click="savePoster">保存分享图</button>
    </van-popup>
  </div>
</template>

<script>
import { Toast } from "vant";
import { buildQrImageUrl } from "@/config/sanGao";
import { getSanGaoStaffInfo } from "@/api/sanGaoStaff";
import { isTaikangStaffMode } from "@/utils/sanGaoStaffMode";
import { buildSalesmanRegistrationPoster } from "./salesman-registration-poster";

const STORAGE_KEYS = {
  info: "san_gao_staff_info",
};

export default {
  name: "SanGaoStaffSalesmanQrcode",
  data() {
    return {
      orgId: "",
      orgName: "",
      link: "",
      qrImageUrl: "",
      qrLoadFailed: false,
      buildingPoster: false,
      showPoster: false,
      posterUrl: "",
      posterLink: "",
      posterOrgName: "",
      inWechat: /MicroMessenger/i.test(navigator.userAgent),
    };
  },
  created() {
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      this.orgId = this.$route.query.orgId || "";
      const cached = localStorage.getItem(STORAGE_KEYS.info);
      if (cached) {
        try {
          const info = JSON.parse(cached);
          if (!this.orgId) this.orgId = info.orgId ? String(info.orgId) : "";
          if (String(info.orgId) === String(this.orgId)) this.orgName = info.orgName || "";
        } catch (e) {
          // 忽略损坏的缓存，继续请求机构信息
        }
      }
      try {
        const res = await getSanGaoStaffInfo();
        if (res && res.code === 200 && res.data && res.data.orgId) {
          if (!this.orgId) this.orgId = String(res.data.orgId);
          if (String(res.data.orgId) === String(this.orgId)) this.orgName = res.data.orgName || this.orgName;
        }
      } catch (e) {
        // 请求失败时保留匹配当前机构的缓存；登录失效由拦截器处理
      }
      if (!this.orgId) {
        Toast("未获取到机构信息，请重新登录");
        return;
      }
      this.buildLink();
    },
    async buildLink() {
      // 分享链接携带机构及当前入口标记，页面二维码和分享图共用此链接。
      const origin = window.location.origin;
      const modeQuery = isTaikangStaffMode(this.$route.query) ? "&taikang=1" : "";
      this.link = `${origin}/sanGao/salesman/login?orgId=${encodeURIComponent(this.orgId)}&type=register${modeQuery}`;
      this.qrImageUrl = await buildQrImageUrl(this.link, 320);
    },
    async copyLink() {
      if (!this.link) {
        Toast("链接未生成");
        return;
      }
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.link);
        } else {
          const input = document.createElement("input");
          input.value = this.link;
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        Toast("链接已复制");
      } catch (e) {
        Toast("复制失败，请长按链接手动复制");
      }
    },
    async downloadQr() {
      if (this.buildingPoster) return;
      if (!this.link) {
        Toast("链接未生成");
        return;
      }
      if (!this.orgName) {
        Toast("机构名称未获取到，请刷新页面重试");
        return;
      }
      this.buildingPoster = true;
      try {
        const link = this.link;
        const orgName = this.orgName;
        if (!this.posterUrl || this.posterLink !== link || this.posterOrgName !== orgName) {
          const image = await buildSalesmanRegistrationPoster(link, orgName, this.orgId);
          if (this._isDestroyed || this.link !== link || this.orgName !== orgName) return;
          this.posterUrl = image;
          this.posterLink = link;
          this.posterOrgName = orgName;
        }
        this.showPoster = true;
        if (!this.inWechat) this.savePoster();
      } catch (e) {
        Toast("分享图生成失败，请重试");
      } finally {
        this.buildingPoster = false;
      }
    },
    savePoster() {
      if (!this.posterUrl || this.posterLink !== this.link || this.posterOrgName !== this.orgName) {
        Toast("请先重新生成分享图");
        return;
      }
      const a = document.createElement("a");
      a.href = this.posterUrl;
      a.download = `三高修复_业务员注册分享图_${this.orgId}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style lang="less" scoped>
.qrcode-page {
  position: relative;
  min-height: 100vh;
  padding: 0 0 32px;
  background: linear-gradient(180deg, #0874ca 0%, #4ba8e0 26%, #dff6ff 56%, #f4fafd 100%);
  color: #163b5c;
}

.qrcode-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  text-shadow: 0 2px 6px rgba(0, 48, 113, 0.3);
}

.back-button {
  position: absolute;
  top: 50%;
  left: 14px;
  font-size: 20px;
  color: #fff;
  cursor: pointer;
  transform: translateY(-50%);
}

.qrcode-card {
  margin: 16px;
  padding: 22px 18px 18px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(12, 91, 147, 0.12);
  text-align: center;
}

.qrcode-hint {
  margin: 0 0 14px;
  font-size: 13px;
  color: #5b7488;
}

.org-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  margin-bottom: 16px;
  padding: 12px;
  border-radius: 10px;
  background: #f1f8fd;
}

.org-label, .org-id { font-size: 12px; color: #6b8395; }
.org-name { font-size: 16px; line-height: 1.5; color: #163b5c; }

.qrcode-image-wrap {
  position: relative;
  display: inline-block;
  margin: 0 auto 16px;
  padding: 10px;
  border: 1px solid #eef4f9;
  border-radius: 12px;
  background: #fafdff;
}

.qrcode-image {
  display: block;
  width: 220px;
  height: 220px;
}

.qrcode-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  font-size: 12px;
  color: #b14545;
  background: #fde0e0;
  border-radius: 12px;
}

.link-block {
  padding: 12px 12px;
  margin-bottom: 14px;
  border-radius: 10px;
  text-align: left;
  background: #f4fafd;
}

.link-label {
  margin: 0 0 4px;
  font-size: 11px;
  color: #7c8d9c;
}

.link-content {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
  color: #163b5c;
}

.action-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  border: 0;
  border-radius: 21px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(100deg, #19a2e5, #146bd2);
  box-shadow: 0 4px 12px rgba(17, 112, 199, 0.18);
  cursor: pointer;
}

.action-button .van-icon {
  margin-right: 6px;
  font-size: 16px;
}

.action-button:active {
  opacity: 0.85;
}

.action-button:disabled { opacity: 0.65; }

.poster-popup { width: 88vw; max-width: 380px; padding: 16px; box-sizing: border-box; }
.poster-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; color: #163b5c; font-size: 15px; }
.poster-header button { border: 0; background: none; color: #1677c8; font-size: 13px; }
.poster-image-wrap { max-height: 62vh; overflow: auto; text-align: center; background: #eef6fc; }
.poster-image { display: block; width: 100%; height: auto; }
.poster-save-tip { margin: 12px 0; text-align: center; font-size: 12px; line-height: 1.6; color: #5b7488; }
.poster-save-button { width: 100%; }

.capture-tip {
  margin: 6px 0 0;
  font-size: 11px;
  color: #7c8d9c;
}
</style>
